import { useEffect, useState, useCallback } from "react";
import { api, fileSrc, formatApiError } from "@/lib/api";
import { SignaturePad } from "@/components/InspectionDialog";
import { ShieldCheck, Loader2, CalendarDays, FileText, Receipt, Download, Phone, Mail, Car, ClipboardCheck, PenLine, CreditCard, X } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "-");
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString("fr-FR", { dateStyle: "medium" }) : "-");

const STATUS_LABELS = {
  brouillon: "Brouillon", envoyee: "Envoyée", envoye: "Envoyé", payee: "Payée", partiellement_payee: "Partielle",
  en_retard: "En retard", annulee: "Annulée", accepte: "Accepté", refuse: "Refusé", en_attente: "En attente",
  confirme: "Confirmé", termine: "Terminé", depose: "Déposé", en_cours: "En cours", annule: "Annulé", a_confirmer: "À confirmer",
};
const TRACK = { recente: "À jour", bientot: "Entretien bientôt", depasse: "Entretien dépassé", archivee: "Archivée" };
const lbl = (s) => STATUS_LABELS[s] || s || "-";
const fmtLong = (k) => new Date(`${k}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export default function ClientPortal() {
  const token = new URLSearchParams(window.location.search).get("token");
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [message, setMessage] = useState("");
  const [signing, setSigning] = useState(null);
  const [notice, setNotice] = useState("");

  const load = useCallback(() => api.get(`/portal/${token}`)
    .then((r) => { setData(r.data); setState("ok"); })
    .catch((e) => { setState("error"); setMessage(formatApiError(e.response?.data?.detail) || "Lien invalide."); }), [token]);

  useEffect(() => {
    if (!token) { setState("error"); setMessage("Lien manquant."); return; }
    const p = new URLSearchParams(window.location.search);
    if (p.get("deposit") === "success" && p.get("sid")) {
      api.get(`/portal/${token}/deposit-status`, { params: { sid: p.get("sid") } })
        .then((r) => setNotice(r.data.paid ? `Merci ! Votre acompte de ${eur(r.data.amount)} a bien été réglé.${r.data.remaining != null ? ` Reste à régler : ${eur(r.data.remaining)}.` : ""}` : r.data.status === "refuse" ? `Paiement refusé : ${r.data.message || "carte refusée"}. Vous pouvez réessayer.` : "Paiement en cours de validation."))
        .catch(() => setNotice("Le paiement n'a pas encore pu être vérifié.")).finally(load);
    } else { if (p.get("deposit") === "cancel") setNotice("Paiement annulé."); load(); }
  }, [token, load]);

  const payDeposit = async (q) => {
    try { const { data: d } = await api.post(`/portal/${token}/quotes/${q.id}/deposit`); window.location.href = d.url; }
    catch (e) { setNotice(formatApiError(e.response?.data?.detail)); }
  };

  if (state === "loading") return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;
  if (state === "error") return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 text-center" data-testid="portal-error">
        <h2 className="font-heading text-xl font-bold text-slate-900">Accès impossible</h2>
        <p className="text-sm text-slate-500 mt-2">{message}</p>
      </div>
    </div>
  );
  const v = data.visibility || {};

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="client-portal">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/20"><ShieldCheck className="w-7 h-7 text-black" /></div>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-emerald-600 font-semibold">{data.company_name}</div>
            <h1 className="font-heading text-2xl font-extrabold text-slate-900">Espace de {data.client_name}</h1>
            <div className="text-xs text-slate-500 flex flex-wrap gap-3 mt-0.5">
              {data.company_phone && <a href={`tel:${data.company_phone}`} className="flex items-center gap-1"><Phone className="w-3 h-3" /> {data.company_phone}</a>}
              {data.company_email && <a href={`mailto:${data.company_email}`} className="flex items-center gap-1"><Mail className="w-3 h-3" /> {data.company_email}</a>}
            </div>
          </div>
        </div>
        {notice && <div className="mb-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm px-4 py-3 flex justify-between gap-2" data-testid="portal-notice"><span>{notice}</span><button onClick={() => setNotice("")}><X className="w-4 h-4" /></button></div>}

        {v.vehicles !== false && data.vehicles?.length > 0 && (
          <Section icon={Car} title="Mes véhicules" testid="portal-vehicles">
            <div className="flex flex-wrap gap-2">{data.vehicles.map((x) => <span key={x.id} className="text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">{x.name || "Véhicule"}{x.plate ? ` · ${x.plate}` : ""}</span>)}</div>
          </Section>
        )}

        {v.appointments !== false && <Section icon={CalendarDays} title="Mes rendez-vous" testid="portal-appointments">
          {data.appointments.length === 0 ? <Empty>Aucun rendez-vous.</Empty> : (
            <div className="space-y-3">
              {data.appointments.map((a) => (
                <div key={a.id} className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid={`portal-appt-${a.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium text-slate-900">{a.title || "Rendez-vous"}</div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium shrink-0">{lbl(a.status)}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{fmtDate(a.start)}</div>
                  {(a.before_photos?.length > 0 || a.after_photos?.length > 0) && (
                    <div className="grid grid-cols-2 gap-4 mt-3"><PhotoRow label="Avant" photos={a.before_photos} /><PhotoRow label="Après" photos={a.after_photos} /></div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>}

        {v.quotes !== false && <Section icon={FileText} title="Mes devis" testid="portal-quotes">
          {data.quotes.length === 0 ? <Empty>Aucun devis.</Empty> : (
            <div className="divide-y divide-slate-100">
              {data.quotes.map((q) => (
                <div key={q.id} className="py-3" data-testid={`portal-quote-${q.id}`}>
                  <DocRow num={q.number} date={q.date} total={q.total} status={lbl(q.status)} pdf={q.pdf_url} />
                  {q.chosen_date ? <div className="text-xs text-emerald-800 font-semibold mt-1" data-testid={`portal-quote-chosen-${q.id}`}>Date choisie : {fmtLong(q.chosen_date)}</div>
                    : q.proposed_dates?.length > 0 && q.can_accept && <div className="text-xs text-slate-600 mt-1" data-testid={`portal-quote-proposed-${q.id}`}>Dates proposées : {q.proposed_dates.map(fmtLong).join(" · ")}</div>}
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {q.accepted_at && <span className="text-xs text-emerald-700" data-testid={`portal-quote-signed-${q.id}`}>Signé en ligne le {fmtDate(q.accepted_at)} par {q.accepted_name}</span>}
                    {q.deposit_paid && <span className="text-xs text-emerald-700 font-semibold" data-testid={`portal-quote-deposit-paid-${q.id}`}>Acompte réglé : {eur(q.deposit_paid_amount)}{q.deposit_remaining != null ? ` · reste ${eur(q.deposit_remaining)}` : ""}</span>}
                    {!q.deposit_paid && q.deposit_status === "refuse" && <span className="text-xs text-red-600" data-testid={`portal-quote-deposit-failed-${q.id}`}>Dernier paiement refusé</span>}
                    {q.can_accept && <button data-testid={`portal-quote-accept-${q.id}`} onClick={() => setSigning(q)} className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-black text-xs font-semibold px-3 py-2"><PenLine className="w-3.5 h-3.5" /> Consulter et signer</button>}
                    {q.can_pay_deposit && <button data-testid={`portal-quote-pay-${q.id}`} onClick={() => payDeposit(q)} className="inline-flex items-center gap-1 rounded-lg bg-slate-900 text-white text-xs font-semibold px-3 py-2"><CreditCard className="w-3.5 h-3.5" /> Payer l'acompte de {eur(q.deposit_amount)}</button>}
                    {q.can_pay_deposit && data.deposit_test_mode && <span className="text-[11px] text-amber-700">Paiement en mode test</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>}

        {v.invoices !== false && <Section icon={Receipt} title="Mes factures" testid="portal-invoices">
          {data.invoices.length === 0 ? <Empty>Aucune facture.</Empty> : (
            <div className="divide-y divide-slate-100">
              {data.invoices.map((i) => <div key={i.id} className="py-3" data-testid={`portal-invoice-${i.id}`}><DocRow num={i.number} date={i.date} total={i.total} status={lbl(i.status)} pdf={i.pdf_url} /></div>)}
            </div>
          )}
        </Section>}

        {v.reports !== false && data.reports?.length > 0 && <Section icon={ClipboardCheck} title="Mes rapports" testid="portal-reports">
          <div className="divide-y divide-slate-100">
            {data.reports.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 py-3" data-testid={`portal-report-${r.id}`}>
                <div className="min-w-0"><div className="text-sm font-medium text-slate-900 break-words">{r.label}</div><div className="text-xs text-slate-500">{fmtDay(r.date)}</div></div>
                <a href={fileSrc(r.pdf_url)} target="_blank" rel="noreferrer" className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline"><Download className="w-3.5 h-3.5" /> PDF</a>
              </div>
            ))}
          </div>
        </Section>}

        {v.protections !== false && data.protections?.length > 0 && <Section icon={ShieldCheck} title="Mes protections et entretiens" testid="portal-protections">
          <div className="space-y-2">
            {data.protections.map((p) => (
              <div key={p.id} className="flex flex-wrap justify-between gap-2 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                <span className="text-slate-800">{p.label || p.type}{p.product ? ` · ${p.product}` : ""}</span>
                <span className="text-xs text-slate-500">{p.maintenance_date ? `Entretien conseillé le ${fmtDay(p.maintenance_date)}` : ""} {TRACK[p.track_status] ? `· ${TRACK[p.track_status]}` : ""}</span>
              </div>
            ))}
          </div>
        </Section>}

        <p className="text-center text-xs text-slate-400 mt-8">Espace client sécurisé — propulsé par Auto Detail Manager</p>
      </div>
      {signing && <SignDialog token={token} quote={signing} onClose={() => setSigning(null)} onDone={(m) => { setSigning(null); setNotice(m); load(); }} />}
    </div>
  );
}

function SignDialog({ token, quote, onClose, onDone }) {
  const [q, setQ] = useState(null); const [name, setName] = useState(""); const [sig, setSig] = useState(null);
  const [ok, setOk] = useState(false); const [err, setErr] = useState(""); const [day, setDay] = useState(""); const [busy, setBusy] = useState(false); const [refuse, setRefuse] = useState(null);
  useEffect(() => { api.get(`/portal/${token}/quotes/${quote.id}`).then((r) => setQ(r.data)).catch((e) => setErr(formatApiError(e.response?.data?.detail))); }, [token, quote.id]);
  const accept = async () => {
    setBusy(true); setErr("");
    try { await api.post(`/portal/${token}/quotes/${quote.id}/accept`, { name, signature: sig || "", accept_terms: ok, chosen_date: day || null }); onDone("Merci ! Votre devis est signé." + (day ? ` Date choisie : ${fmtLong(day)}.` : "") + (q?.deposit_amount ? " Vous pouvez maintenant régler l'acompte." : "")); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doRefuse = async () => {
    setBusy(true);
    try { await api.post(`/portal/${token}/quotes/${quote.id}/refuse`, { reason: refuse }); onDone("Votre réponse a bien été transmise."); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-5 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="portal-sign-dialog">
        <div className="flex justify-between items-center mb-3"><h3 className="font-heading text-lg font-bold">Devis {quote.number}</h3><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        {!q ? <Loader2 className="w-5 h-5 animate-spin text-emerald-600" /> : <>
          <div className="space-y-1 mb-3">{q.items.map((it, i) => <div key={i} className="flex justify-between gap-2 text-sm"><span className="text-slate-700 break-words">{it.quantity} × {it.description}</span><span className="font-mono shrink-0">{eur((it.quantity || 0) * (it.unit_price || 0))}</span></div>)}</div>
          <div className="flex justify-between font-semibold border-t pt-2 mb-1"><span>Total</span><span data-testid="portal-sign-total">{eur(q.total)}</span></div>
          {q.deposit_amount ? <div className="text-xs text-slate-500 mb-2">Acompte demandé à la signature : {eur(q.deposit_amount)}</div> : null}
          {q.valid_until && <div className="text-xs text-slate-500 mb-2">Valable jusqu'au {fmtDay(q.valid_until)}</div>}
          {q.terms && <p className="text-xs text-slate-500 whitespace-pre-line mb-3">{q.terms}</p>}
          {refuse === null ? <>
            {q.proposed_dates?.length > 0 && (
              <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50/50 p-3" data-testid="portal-sign-dates">
                <div className="text-sm font-semibold text-slate-900 mb-2">Choisissez votre date parmi les disponibilités proposées</div>
                {q.proposed_dates.map((k) => (
                  <label key={k} className="flex items-center gap-2 text-sm py-1"><input type="radio" name="proposed-date" value={k} checked={day === k} onChange={() => setDay(k)} data-testid={`portal-sign-date-${k}`} /> {cap(fmtLong(k))}</label>
                ))}
              </div>
            )}
            <label className="block text-xs text-slate-500 mb-1">Nom et prénom du signataire</label>
            <input data-testid="portal-sign-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm mb-3" />
            <SignaturePad value={sig} onChange={setSig} />
            <label className="flex items-start gap-2 text-sm mt-3"><input type="checkbox" data-testid="portal-sign-terms" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-1" /> <span>Bon pour accord : j'accepte ce devis et ses conditions.</span></label>
            {err && <p className="text-sm text-red-600 mt-2" data-testid="portal-sign-error">{err}</p>}
            <div className="flex flex-col-reverse sm:flex-row gap-2 mt-4">
              <button onClick={() => setRefuse("")} data-testid="portal-refuse-open" className="flex-1 rounded-lg bg-slate-100 text-slate-700 text-sm font-semibold py-2.5">Refuser</button>
              <button onClick={accept} disabled={busy || !sig || !ok || name.trim().length < 2 || (q.proposed_dates?.length > 0 && !day)} data-testid="portal-sign-submit" className="flex-1 rounded-lg bg-emerald-500 text-black text-sm font-semibold py-2.5 disabled:opacity-50">Signer le devis</button>
            </div>
          </> : <>
            <label className="block text-xs text-slate-500 mb-1">Motif (facultatif)</label>
            <textarea data-testid="portal-refuse-reason" value={refuse} onChange={(e) => setRefuse(e.target.value)} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm" rows={3} />
            <div className="flex gap-2 mt-3"><button onClick={() => setRefuse(null)} className="flex-1 rounded-lg bg-slate-100 text-sm py-2.5">Retour</button><button onClick={doRefuse} disabled={busy} data-testid="portal-refuse-submit" className="flex-1 rounded-lg bg-red-500 text-white text-sm font-semibold py-2.5">Confirmer le refus</button></div>
          </>}
        </>}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, testid, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 mb-6" data-testid={testid}>
      <h2 className="font-heading text-base font-bold text-slate-900 mb-4 flex items-center gap-2"><Icon className="w-4 h-4 text-emerald-600" /> {title}</h2>
      {children}
    </div>
  );
}

function Empty({ children }) { return <p className="text-sm text-slate-500">{children}</p>; }

function PhotoRow({ label, photos }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-2">
        {(photos || []).length === 0 ? <span className="text-xs text-slate-400">—</span> : photos.map((p, i) => (
          <a key={i} href={fileSrc(p.url || p.path)} target="_blank" rel="noreferrer" className="w-14 h-14 rounded-md overflow-hidden border border-slate-200">
            <img src={fileSrc(p.url || p.path)} alt="" className="w-full h-full object-cover" />
          </a>
        ))}
      </div>
    </div>
  );
}

function DocRow({ num, date, total, status, pdf }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-900">{num}</div>
        <div className="text-xs text-slate-500">{fmtDay(date)} · {status}</div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="font-mono font-semibold text-slate-900 text-sm">{eur(total)}</span>
        <a href={fileSrc(pdf)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline"><Download className="w-3.5 h-3.5" /> PDF</a>
      </div>
    </div>
  );
}
